import { useMemo, useState } from 'react';
import {
  Anchor,
  Badge,
  Card,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import { useSession } from '../../app/session';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';

type TaxonomyTab = 'licences' | 'ratings' | 'certificates' | 'medicals';

/**
 * The configuration lists, read-only here.
 *
 * Editing taxonomies is a rare, careful act with translated names per language, which the
 * Unfold admin already does properly, so this screen shows what is configured and links there
 * rather than reimplementing a worse version of it.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/$org/settings' });
  const { organisation, user } = useSession();
  const org = organisation!.slug;

  const tab = search.tab ?? 'taxonomies';
  const setTab = (value: string | null) =>
    void navigate({
      to: '/$org/settings',
      params: { org },
      search: { tab: value ?? 'taxonomies' },
    });

  return (
    <>
      <PageHeader title={t('settings.title')} subtitle={organisation?.name} />

      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List>
          <Tabs.Tab value="taxonomies">{t('settings.taxonomies')}</Tabs.Tab>
          <Tabs.Tab value="roles">{t('settings.roles')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'taxonomies' ? <TaxonomyPanel /> : null}
      {tab === 'roles' ? <RolePanel /> : null}

      {user?.is_staff ? (
        <Anchor href="/admin/" target="_blank" rel="noreferrer" fz="sm" mt="lg" display="block">
          <Group gap={6} wrap="nowrap">
            {t('nav.admin')}
            <IconExternalLink size={14} />
          </Group>
        </Anchor>
      ) : null}
    </>
  );
}

function TaxonomyPanel() {
  const { t } = useTranslation();
  const [which, setWhich] = useState<TaxonomyTab>('licences');

  const licences = useQuery({
    queryKey: ['taxonomy', 'licence-types'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/taxonomy/licence-types/', {
          params: { query: { page_size: 200, ordering: 'sort_order,code' } },
        }),
      ),
    enabled: which === 'licences',
  });

  const ratings = useQuery({
    queryKey: ['taxonomy', 'rating-types'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/taxonomy/rating-types/', {
          params: { query: { page_size: 300, ordering: 'sort_order,code' } },
        }),
      ),
    enabled: which === 'ratings',
  });

  const certificates = useQuery({
    queryKey: ['taxonomy', 'certificate-types'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/taxonomy/certificate-types/', {
          params: { query: { page_size: 200, ordering: 'sort_order,code' } },
        }),
      ),
    enabled: which === 'certificates',
  });

  const medicals = useQuery({
    queryKey: ['taxonomy', 'medical-classes'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/taxonomy/medical-classes/', {
          params: { query: { page_size: 100, ordering: 'sort_order,code' } },
        }),
      ),
    enabled: which === 'medicals',
  });

  const rows = useMemo(() => {
    switch (which) {
      case 'licences':
        return (licences.data?.results ?? []).map((row) => ({
          id: row.id,
          code: row.code,
          name: row.name,
          detail: row.discipline,
          isGlobal: row.organisation === null,
        }));
      case 'ratings':
        return (ratings.data?.results ?? []).map((row) => ({
          id: row.id,
          code: row.code,
          name: row.name,
          detail: [row.kind, row.revalidation_basis].filter(Boolean).join(' · '),
          isGlobal: row.organisation === null,
        }));
      case 'certificates':
        return (certificates.data?.results ?? []).map((row) => ({
          id: row.id,
          code: row.code,
          name: row.name,
          detail: row.category,
          isGlobal: row.organisation === null,
        }));
      case 'medicals':
        return (medicals.data?.results ?? []).map((row) => ({
          id: row.id,
          code: row.code,
          name: row.name,
          detail: row.short_name ?? '',
          isGlobal: row.organisation === null,
        }));
    }
  }, [which, licences.data, ratings.data, certificates.data, medicals.data]);

  return (
    <Stack gap="md">
      <SegmentedControl
        value={which}
        onChange={(value) => setWhich(value as TaxonomyTab)}
        data={[
          { value: 'licences', label: t('settings.licenceTypes') },
          { value: 'ratings', label: t('settings.ratingTypes') },
          { value: 'certificates', label: t('settings.certificateTypes') },
          { value: 'medicals', label: t('settings.medicalClasses') },
        ]}
      />

      <Card p="md">
        {rows.length === 0 ? (
          <EmptyState title={t('common.noResults')} />
        ) : (
          <Table fz="sm" verticalSpacing={6} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={110}>{t('settings.code')}</Table.Th>
                <Table.Th>{t('common.name')}</Table.Th>
                <Table.Th w={220}>{t('common.summary')}</Table.Th>
                <Table.Th w={190} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td fw={600} ff="monospace">
                    {row.code}
                  </Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td c="dimmed" fz="xs">
                    {row.detail}
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      variant="outline"
                      color={row.isGlobal ? 'gray' : 'teal'}
                    >
                      {row.isGlobal ? t('settings.globalEntry') : t('settings.clubEntry')}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Text fz="xs" c="dimmed">
        {t('settings.taxonomyHint')}
      </Text>
    </Stack>
  );
}

function RolePanel() {
  const { t } = useTranslation();

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/roles/', { params: { query: { page_size: 100, ordering: 'name' } } }),
      ),
  });

  return (
    <Stack gap="md">
      {(roles.data?.results ?? []).map((role) => (
        <Card key={role.id} p="md">
          <Group justify="space-between" mb="xs">
            <Group gap="sm">
              <Text fw={600}>{role.name}</Text>
              <Badge size="xs" variant="outline" ff="monospace">
                {role.code}
              </Badge>
            </Group>
            <Badge
              size="xs"
              variant="outline"
              color={role.organisation === null ? 'gray' : 'teal'}
            >
              {role.organisation === null ? t('settings.globalEntry') : t('settings.clubEntry')}
            </Badge>
          </Group>
          <Group gap={4} wrap="wrap">
            {(role.permissions ?? []).map((permission) => (
              <Badge key={permission} size="xs" variant="light" color="gray">
                {permission}
              </Badge>
            ))}
          </Group>
        </Card>
      ))}
      {(roles.data?.results ?? []).length === 0 ? (
        <EmptyState title={t('settings.roles')} />
      ) : null}
    </Stack>
  );
}
